import React, { useEffect, useState, useRef } from 'react';
import { api, auth } from './auth';

const blank = {
  name: '', primary_colour: '#111827',
  monthly_fee_cents: 0, user_monthly_cents: 0,
  voice_mandate_cents: 500, pdf_mandate_cents: 300,
  monthly_capture_fee_cents: 0,
  bill_monthly_fee: true, bill_per_user: false,
  bill_voice_mandates: true, bill_pdf_mandates: true,
  bill_capture_monthly: false,
  billing_mode: 'byok',
  saas_tier: '',
  capture_enabled: false,
  retention_days: null,
  lockout_max_attempts: null,
  lockout_window_minutes: null,
  require_mfa: false,
};

export default function AdminCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [managingKeysCo, setManagingKeysCo] = useState(null);
  const logoRef = useRef();

  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/companies'); setCompanies(r.data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleLogoChange = (file) => {
    if (!file) { setLogoFile(null); setLogoPreview(null); return; }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      let companyId;
      const body = { ...form, retention_use_default: form.retention_days == null };
      if (editing) {
        await api.patch(`/admin/companies/${editing.id}`, body);
        companyId = editing.id;
      } else {
        const r = await api.post('/admin/companies', body);
        companyId = r.data.id;
      }
      if (logoFile) {
        const fd = new FormData(); fd.append('file', logoFile);
        await api.post(`/admin/companies/${companyId}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        const currentUser = auth.getUser();
        if (currentUser?.company_id === companyId) {
          const meRes = await api.get('/auth/me');
          auth.setSession(auth.getToken(), meRes.data);
          window.location.reload(); return;
        }
      }
      setShowForm(false); setEditing(null); setForm(blank);
      setLogoFile(null); setLogoPreview(null);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Save failed');
    } finally { setSubmitting(false); }
  };

  const startEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, primary_colour: c.primary_colour,
      monthly_fee_cents: c.monthly_fee_cents, user_monthly_cents: c.user_monthly_cents,
      voice_mandate_cents: c.voice_mandate_cents, pdf_mandate_cents: c.pdf_mandate_cents,
      monthly_capture_fee_cents: c.monthly_capture_fee_cents || 0,
      bill_monthly_fee: c.bill_monthly_fee, bill_per_user: c.bill_per_user,
      bill_voice_mandates: c.bill_voice_mandates, bill_pdf_mandates: c.bill_pdf_mandates,
      bill_capture_monthly: c.bill_capture_monthly || false,
      billing_mode: c.billing_mode || 'byok',
      saas_tier: c.saas_tier || '',
      capture_enabled: !!c.capture_enabled,
      retention_days: c.retention_days ?? null,
      lockout_max_attempts: c.lockout_max_attempts ?? null,
      lockout_window_minutes: c.lockout_window_minutes ?? null,
      require_mfa: !!c.require_mfa,
    });
    setLogoFile(null);
    setLogoPreview(c.has_logo ? `${process.env.REACT_APP_API_URL || ''}/companies/${c.id}/logo?t=${Date.now()}` : null);
    setShowForm(true);
  };

  const cancel = () => {
    setShowForm(false); setEditing(null); setForm(blank);
    setLogoFile(null); setLogoPreview(null);
  };

  const deactivate = async (cid, name) => {
    if (!window.confirm(`Deactivate ${name}? This will deactivate all company users and revoke pending invitations.`)) return;
    await api.patch(`/admin/companies/${cid}/deactivate`);
    load();
  };

  const reactivate = async (cid) => {
    if (!window.confirm('Activate this company and all its users? They will be able to sign in.')) return;
    await api.patch(`/admin/companies/${cid}/reactivate`);
    load();
  };

  const extendTrial = async (cid, name) => {
    const input = window.prompt(`Extend the trial for ${name} by how many days?`, '14');
    if (input === null) return;
    const days = parseInt(input, 10);
    if (!Number.isFinite(days) || days <= 0) { alert('Enter a positive number of days.'); return; }
    try {
      const r = await api.post(`/admin/companies/${cid}/extend-trial`, { days });
      const ends = new Date(r.data.trial_ends_at).toLocaleDateString();
      alert(`Trial for ${r.data.company_name} now ends ${ends} (status: ${r.data.subscription_status}).`);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || err.message || 'Could not extend the trial.');
    }
  };

  const fmtR = (c) => `R${(c / 100).toFixed(2)}`;

  if (managingKeysCo) {
    return <CompanyKeysPanel
      company={managingKeysCo}
      onBack={() => { setManagingKeysCo(null); load(); }}
    />;
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>Companies</h2>
        <button onClick={() => { setEditing(null); setForm(blank); setLogoFile(null); setLogoPreview(null); setShowForm(true); }} style={btnPrimary}>+ New company</button>
      </div>

      {showForm && (
        <form onSubmit={submit} style={{ background: '#F8F9FB', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #E5E7EB' }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 14px 0' }}>{editing ? 'Edit company' : 'New company'}</h3>

          <Section title="Company details">
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                <label style={{ fontSize: 11, color: '#6B7280' }}>Logo</label>
                <div onClick={() => logoRef.current.click()} style={{
                  width: 110, height: 110, borderRadius: 12,
                  background: logoPreview ? '#fff' : '#F3F4F6',
                  border: `2px dashed ${logoPreview ? 'transparent' : '#D1D5DB'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', overflow: 'hidden',
                }}>
                  {logoPreview
                    ? <img src={logoPreview} alt="" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain' }} />
                    : <div style={{ textAlign: 'center', color: '#9CA3AF' }}>
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                        <p style={{ fontSize: 11, margin: '4px 0 0 0' }}>Upload</p>
                      </div>}
                  <input ref={logoRef} type="file" accept="image/png,image/jpeg,image/svg+xml" style={{ display: 'none' }} onChange={e => handleLogoChange(e.target.files[0])} />
                </div>
                {logoPreview && <button type="button" onClick={() => handleLogoChange(null)} style={{ ...btnGhost, fontSize: 11, padding: '3px 8px', color: '#DC2626' }}>Remove</button>}
                <p style={{ fontSize: 10, color: '#9CA3AF', margin: 0 }}>PNG, JPG, SVG</p>
              </div>

              <div style={{ flex: 1, minWidth: 280, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 12 }}>
                <Field label="Company name">
                  <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required style={inp} />
                </Field>
                <Field label="Brand colour">
                  <input type="color" value={form.primary_colour} onChange={e => setForm({ ...form, primary_colour: e.target.value })} style={{ ...inp, padding: 4, height: 38, cursor: 'pointer' }} />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="SaaS tier">
            <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
              Display-only label for sales reporting and invoicing reference. Not enforced by the platform.
            </p>
            <Field label="Tier">
              <select value={form.saas_tier || ''} onChange={e => setForm({ ...form, saas_tier: e.target.value })} style={inp}>
                <option value="">Select...</option>
                <option value="basic">Basic</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </Field>
            <p style={{ fontSize: 11, color: '#6B7280', marginTop: 8, marginBottom: 0 }}>
              After saving, the customer admin signs in and adds their own OpenAI &amp; Anthropic API keys via <strong>Account → API Keys</strong>. Or super-admin can pre-load keys via the <strong>Keys</strong> button on this company in the table below.
            </p>
          </Section>

          <Section title="Security and retention">
            <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>Per-company overrides. Leave retention on the platform default unless this customer needs something different.</p>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.retention_days == null}
                onChange={e => setForm({ ...form, retention_days: e.target.checked ? null : 7 })}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#111827' }}>Use platform default report retention</span>
            </label>
            {form.retention_days != null && (
              <Field label="Report retention (days)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="number" min={1} max={3650} step={1} value={form.retention_days}
                    onChange={e => setForm({ ...form, retention_days: parseInt(e.target.value) || 1 })}
                    style={{ ...inp, width: 140 }} />
                  <span style={{ fontSize: 11, color: '#9CA3AF' }}>days before a report file is deleted</span>
                </div>
              </Field>
            )}

            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, marginBottom: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.lockout_max_attempts == null && form.lockout_window_minutes == null}
                onChange={e => setForm({ ...form, lockout_max_attempts: e.target.checked ? null : 5, lockout_window_minutes: e.target.checked ? null : 15 })}
                style={{ width: 16, height: 16, cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: '#111827' }}>Use platform default login lockout policy</span>
            </label>
            {(form.lockout_max_attempts != null || form.lockout_window_minutes != null) && (
              <>
                <Field label="Login lockout threshold">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" min={1} max={50} step={1} value={form.lockout_max_attempts ?? 5}
                      onChange={e => setForm({ ...form, lockout_max_attempts: parseInt(e.target.value) || 1 })}
                      style={{ ...inp, width: 140 }} />
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>failed attempts before the account locks</span>
                  </div>
                </Field>
                <Field label="Lockout window (minutes)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input type="number" min={1} max={1440} step={1} value={form.lockout_window_minutes ?? 15}
                      onChange={e => setForm({ ...form, lockout_window_minutes: parseInt(e.target.value) || 1 })}
                      style={{ ...inp, width: 140 }} />
                    <span style={{ fontSize: 11, color: '#9CA3AF' }}>minutes the lock lasts, and the window attempts are counted over</span>
                  </div>
                </Field>
              </>
            )}
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginTop: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!form.require_mfa}
                onChange={e => setForm({ ...form, require_mfa: e.target.checked })}
                style={{ width: 16, height: 16, marginTop: 2, cursor: 'pointer' }} />
              <span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>Require 2FA for all users in this company</span>
                <span style={{ display: 'block', fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>When on, users must set up an authenticator app to sign in. Sign-in enforcement activates with the enrollment gate.</span>
              </span>
            </label>
          </Section>

          <Section title="Billing configuration">
            <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
              Customer brings their own AI keys and pays OpenAI &amp; Anthropic directly. Eyez bills a monthly SaaS subscription plus a small per-mandate orchestration fee.
            </p>
            <BillingRow enabled={form.bill_monthly_fee} onToggle={v => setForm({ ...form, bill_monthly_fee: v })} label="Monthly subscription fee" hint="Charged once per month per company" value={form.monthly_fee_cents} onChange={v => setForm({ ...form, monthly_fee_cents: v })} />
            <BillingRow enabled={form.bill_per_user} onToggle={v => setForm({ ...form, bill_per_user: v })} label="Per active user / month" hint="Multiplied by active user count each month" value={form.user_monthly_cents} onChange={v => setForm({ ...form, user_monthly_cents: v })} />
            <BillingRow enabled={form.bill_voice_mandates} onToggle={v => setForm({ ...form, bill_voice_mandates: v })} label="Per voice mandate verification" hint="Charged each time a voice recording is verified" value={form.voice_mandate_cents} onChange={v => setForm({ ...form, voice_mandate_cents: v })} />
            <BillingRow enabled={form.bill_pdf_mandates} onToggle={v => setForm({ ...form, bill_pdf_mandates: v })} label="Per written/PDF mandate verification" hint="Charged each time a PDF is verified" value={form.pdf_mandate_cents} onChange={v => setForm({ ...form, pdf_mandate_cents: v })} />
          </Section>

          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            <button type="submit" disabled={submitting} style={{ ...btnPrimary, opacity: submitting ? 0.6 : 1 }}>{submitting ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
            <button type="button" onClick={cancel} style={btnGhost}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? <p style={{ color: '#9CA3AF' }}>Loading…</p>
       : companies.length === 0 ? <p style={{ color: '#9CA3AF' }}>No companies yet.</p>
       : (
        <div style={{ overflowX: 'auto' }}>
          <table style={tbl}>
            <thead><tr style={{ background: '#F8F9FB', textAlign: 'left' }}>
              <th style={th}>Logo</th><th style={th}>Company</th><th style={th}>Status</th>
              <th style={th}>Tier</th>
              <th style={th}>Monthly</th><th style={th}>Per user</th>
              <th style={th}>Voice</th><th style={th}>PDF</th>
              <th style={th}>Users</th><th style={th}>Verifs</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {companies.map(c => (
                <tr key={c.id} style={{ borderBottom: '0.5px solid #F3F4F6', opacity: c.is_active ? 1 : 0.55 }}>
                  <td style={td}>
                    {c.has_logo
                      ? <img src={`${process.env.REACT_APP_API_URL || ''}/companies/${c.id}/logo`} alt="" style={{ height: 28, maxWidth: 60, objectFit: 'contain' }} />
                      : <span style={{ display: 'inline-block', width: 28, height: 28, borderRadius: 6, background: c.primary_colour }} />}
                  </td>
                  <td style={td}><strong>{c.name}</strong><br /><span style={{ fontSize: 11, color: '#9CA3AF' }}>/{c.slug}</span></td>
                  <td style={td}>
                    {c.is_active
                      ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#DCFCE7', color: '#166534', fontWeight: 600 }}>ACTIVE</span>
                      : (!c.deactivated_at
                          ? <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#FEF3C7', color: '#92400E', fontWeight: 600 }}>PENDING</span>
                          : <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: '#FEE2E2', color: '#991B1B', fontWeight: 600 }}>INACTIVE</span>)}
                  </td>
                  <td style={td}>
                    {c.saas_tier && <div style={{ fontSize: 11, color: '#374151', textTransform: 'capitalize', fontWeight: 500 }}>{c.saas_tier}</div>}
                    <div style={{ fontSize: 10, marginTop: 2 }}>
                      {c.has_openai_key && c.has_anthropic_key
                        ? <span style={{ color: '#16A34A' }}>● keys set</span>
                        : <span style={{ color: '#DC2626' }}>● keys missing</span>}
                    </div>
                  </td>
                  <td style={td}>{c.bill_monthly_fee ? fmtR(c.monthly_fee_cents) : <span style={off}>off</span>}</td>
                  <td style={td}>{c.bill_per_user ? fmtR(c.user_monthly_cents) : <span style={off}>off</span>}</td>
                  <td style={td}>{c.bill_voice_mandates ? fmtR(c.voice_mandate_cents) : <span style={off}>off</span>}</td>
                  <td style={td}>{c.bill_pdf_mandates ? fmtR(c.pdf_mandate_cents) : <span style={off}>off</span>}</td>
                  <td style={td}>{c.user_count}</td>
                  <td style={td}>{c.verification_count}</td>
                  <td style={td}>
                    <button onClick={() => startEdit(c)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>Edit</button>
                    <button onClick={() => setManagingKeysCo(c)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', marginLeft: 4, color: '#1E40AF' }}>Keys</button>
                    <button onClick={() => extendTrial(c.id, c.name)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', marginLeft: 4, color: '#7C3AED' }}>Extend trial</button>
                    {c.is_active
                      ? <button onClick={() => deactivate(c.id, c.name)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', marginLeft: 4, color: '#DC2626' }}>Deactivate</button>
                      : <button onClick={() => reactivate(c.id)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', marginLeft: 4, color: '#16A34A' }}>{c.deactivated_at ? 'Reactivate' : 'Activate'}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return <div style={{ marginBottom: 16 }}>
    <p style={{ fontSize: 11, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>{title}</p>
    {children}
  </div>;
}
function Field({ label, children }) {
  return <div>
    <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>{label}</label>
    {children}
  </div>;
}
function BillingRow({ enabled, onToggle, label, hint, value, onChange }) {
  return <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '0.5px solid #F3F4F6' }}>
    <input type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
    <div style={{ flex: 1 }}>
      <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: enabled ? '#111827' : '#9CA3AF' }}>{label}</p>
      <p style={{ fontSize: 11, color: '#9CA3AF', margin: '2px 0 0 0' }}>{hint}</p>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, opacity: enabled ? 1 : 0.4 }}>
      <span style={{ fontSize: 12, color: '#6B7280' }}>R</span>
      <input type="number" min={0} step={0.01} value={(value / 100).toFixed(2)} onChange={e => onChange(Math.round(parseFloat(e.target.value || 0) * 100))} disabled={!enabled} style={{ ...inp, width: 100, textAlign: 'right' }} />
      <span style={{ fontSize: 11, color: '#9CA3AF' }}>({value}c)</span>
    </div>
  </div>;
}

const off = { color: '#D1D5DB', fontSize: 11, textTransform: 'uppercase' };
const card = { background: '#fff', borderRadius: 16, border: '1px solid #E5E7EB', padding: '20px 24px' };
const btnPrimary = { background: '#111827', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 500 };
const btnGhost = { background: 'transparent', color: '#374151', border: '1px solid #E5E7EB', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 };
const inp = { padding: '8px 10px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' };
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 };
const th = { padding: '10px 12px', fontWeight: 500, fontSize: 11, color: '#6B7280', textTransform: 'uppercase', borderBottom: '1px solid #E5E7EB' };
const td = { padding: '10px 12px', color: '#111827' };

function CompanyKeysPanel({ company, onBack }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openaiInput, setOpenaiInput] = useState('');
  const [anthropicInput, setAnthropicInput] = useState('');
  const [showOpenai, setShowOpenai] = useState(false);
  const [showAnthropic, setShowAnthropic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/companies/${company.id}/keys`);
      setStatus(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load key status');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [company.id]);

  const saveKeys = async () => {
    if (!openaiInput && !anthropicInput) {
      alert('Enter at least one key to save');
      return;
    }
    setSaving(true); setTestResult(null);
    try {
      const payload = {};
      if (openaiInput) payload.openai_api_key = openaiInput;
      if (anthropicInput) payload.anthropic_api_key = anthropicInput;
      await api.patch(`/admin/companies/${company.id}/keys`, payload);
      setOpenaiInput(''); setAnthropicInput('');
      setShowOpenai(false); setShowAnthropic(false);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const testKeys = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await api.post(`/admin/companies/${company.id}/keys/test`);
      setTestResult(r.data);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const clearKey = async (provider) => {
    const human = provider === 'all' ? 'BOTH keys' : `the ${provider} key`;
    if (!window.confirm(`Clear ${human}? Verifications for this company will fail until new keys are entered.`)) return;
    try {
      await api.delete(`/admin/companies/${company.id}/keys?provider=${provider}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Clear failed');
    }
  };

  const StatusBadge = ({ ok, label }) => (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700,
      background: ok ? '#DCFCE7' : '#FEE2E2',
      color: ok ? '#166534' : '#991B1B',
    }}>{label}</span>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <button onClick={onBack} style={{ ...btnGhost, marginBottom: 6 }}>← Back to companies</button>
          <h2 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>API Keys: {company.name}</h2>
          <p style={{ fontSize: 12, color: '#6B7280', margin: '4px 0 0 0' }}>
            BYOK mode {company.saas_tier && <>· tier: <strong style={{ textTransform: 'capitalize' }}>{company.saas_tier}</strong></>}
          </p>
        </div>
      </div>

      {loading || !status ? <p style={{ color: '#9CA3AF' }}>Loading…</p> : (
        <>
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#92400E' }}>
            <strong>Sensitive: handle with care.</strong> Keys are encrypted at rest with Fernet (AES-128). Once entered, the plaintext is never displayed in the UI again, only the prefix and last 4 characters. Keys are only used for this company's verifications and are never logged.
          </div>

          <Section title="Current keys">
            <div style={{ background: '#F8F9FB', borderRadius: 10, padding: 14, border: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '0.5px solid #E5E7EB' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>OpenAI <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>(used for Whisper voice transcription)</span></div>
                  <div style={{ fontSize: 12, color: status.has_openai_key ? '#374151' : '#9CA3AF', fontFamily: 'monospace', marginTop: 2 }}>
                    {status.has_openai_key ? status.openai_key_masked : '(not set)'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <StatusBadge ok={status.has_openai_key} label={status.has_openai_key ? 'SET' : 'MISSING'} />
                  {status.has_openai_key && (
                    <button onClick={() => clearKey('openai')} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', color: '#DC2626' }}>Clear</button>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0' }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>Anthropic <span style={{ fontSize: 10, color: '#6B7280', fontWeight: 400 }}>(used for field extraction, multi-mandate detection, OCR)</span></div>
                  <div style={{ fontSize: 12, color: status.has_anthropic_key ? '#374151' : '#9CA3AF', fontFamily: 'monospace', marginTop: 2 }}>
                    {status.has_anthropic_key ? status.anthropic_key_masked : '(not set)'}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <StatusBadge ok={status.has_anthropic_key} label={status.has_anthropic_key ? 'SET' : 'MISSING'} />
                  {status.has_anthropic_key && (
                    <button onClick={() => clearKey('anthropic')} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px', color: '#DC2626' }}>Clear</button>
                  )}
                </div>
              </div>
            </div>

            {status.keys_set_at && (
              <p style={{ fontSize: 11, color: '#6B7280', marginTop: 8 }}>
                Keys last set: {new Date(status.keys_set_at).toLocaleString()}.
                {status.keys_last_tested_at && (
                  <> Last tested: {new Date(status.keys_last_tested_at).toLocaleString()}, {' '}
                  {status.keys_last_test_status === 'ok'
                    ? <span style={{ color: '#16A34A', fontWeight: 600 }}>OK</span>
                    : <span style={{ color: '#DC2626', fontWeight: 600 }}>{status.keys_last_test_status?.replace('_', ' ')}</span>}
                  </>
                )}
              </p>
            )}
            {status.keys_last_test_detail && status.keys_last_test_status !== 'ok' && (
              <div style={{ marginTop: 6, padding: 8, background: '#FEE2E2', borderRadius: 6, fontSize: 11, color: '#991B1B' }}>
                {status.keys_last_test_detail}
              </div>
            )}
          </Section>

          <Section title={status.has_openai_key || status.has_anthropic_key ? 'Rotate or update keys' : 'Add keys'}>
            <p style={{ fontSize: 11, color: '#6B7280', marginBottom: 10 }}>
              Enter a new key to replace the existing one for that provider. Leave blank to keep current.
            </p>
            <Field label="OpenAI API key (sk-…)">
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type={showOpenai ? 'text' : 'password'}
                  value={openaiInput}
                  onChange={e => setOpenaiInput(e.target.value)}
                  placeholder="sk-..."
                  autoComplete="new-password"
                  style={{ ...inp, fontFamily: 'monospace' }}
                />
                <button type="button" onClick={() => setShowOpenai(!showOpenai)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>{showOpenai ? 'Hide' : 'Show'}</button>
              </div>
            </Field>
            <div style={{ height: 8 }} />
            <Field label="Anthropic API key (sk-ant-…)">
              <div style={{ display: 'flex', gap: 6 }}>
                <input
                  type={showAnthropic ? 'text' : 'password'}
                  value={anthropicInput}
                  onChange={e => setAnthropicInput(e.target.value)}
                  placeholder="sk-ant-..."
                  autoComplete="new-password"
                  style={{ ...inp, fontFamily: 'monospace' }}
                />
                <button type="button" onClick={() => setShowAnthropic(!showAnthropic)} style={{ ...btnGhost, fontSize: 11, padding: '4px 10px' }}>{showAnthropic ? 'Hide' : 'Show'}</button>
              </div>
            </Field>

            <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={saveKeys} disabled={saving || (!openaiInput && !anthropicInput)} style={{ ...btnPrimary, opacity: (saving || (!openaiInput && !anthropicInput)) ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save keys'}
              </button>
              {status.has_openai_key && status.has_anthropic_key && (
                <button onClick={testKeys} disabled={testing} style={{ ...btnGhost, opacity: testing ? 0.6 : 1 }}>
                  {testing ? 'Testing…' : 'Test connection'}
                </button>
              )}
              {(status.has_openai_key || status.has_anthropic_key) && (
                <button onClick={() => clearKey('all')} style={{ ...btnGhost, color: '#DC2626' }}>Clear both</button>
              )}
            </div>

            {testResult && (
              <div style={{
                marginTop: 14, padding: 12, borderRadius: 8,
                background: testResult.overall_status === 'ok' ? '#F0FDF4' : '#FEE2E2',
                border: `1px solid ${testResult.overall_status === 'ok' ? '#BBF7D0' : '#FECACA'}`,
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: testResult.overall_status === 'ok' ? '#166534' : '#991B1B', marginBottom: 4 }}>
                  {testResult.overall_status === 'ok' ? '✓ Both keys working' : '✗ Test failed'}
                </div>
                <div style={{ fontSize: 11, color: '#374151' }}>
                  OpenAI: {testResult.openai_ok ? '✓' : '✗'} {testResult.openai_msg}<br />
                  Anthropic: {testResult.anthropic_ok ? '✓' : '✗'} {testResult.anthropic_msg}
                </div>
              </div>
            )}
          </Section>

          <div style={{ marginTop: 20, padding: 14, background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 8 }}>
            <h4 style={{ fontSize: 12, fontWeight: 700, color: '#075985', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: 0.5 }}>How to get keys</h4>
            <p style={{ fontSize: 12, color: '#374151', margin: '0 0 6px 0', lineHeight: 1.5 }}>
              <strong>OpenAI:</strong> sign in at <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" style={{ color: '#0284C7' }}>platform.openai.com/api-keys</a> → "Create new secret key" → copy and paste here. Used only for Whisper transcription.
            </p>
            <p style={{ fontSize: 12, color: '#374151', margin: 0, lineHeight: 1.5 }}>
              <strong>Anthropic:</strong> sign in at <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer" style={{ color: '#0284C7' }}>console.anthropic.com/settings/keys</a> → "Create Key" → copy and paste here.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
